import { Heart, Coffee, MapPin, Truck, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function WakeWithDrake() {
  const [showOrderModal, setShowOrderModal] = useState(false);
  const isSoldOut = false;

  const scrollToDetails = () => {
    const detailsSection = document.getElementById('details-section');
    detailsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleOrderClick = () => {
    if (isSoldOut) return;
    setShowOrderModal(true);
  };

  const handleCloseModal = () => {
    setShowOrderModal(false);
  };

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        {/* Background with parallax effect */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=1920&q=80)',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#132947]/80 via-[#132947]/90 to-[#132947]" />
        </div>

        {/* Enhanced decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-red-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-red-500/5 to-transparent rounded-full blur-2xl" />

        <div className="relative min-h-screen flex items-center justify-center px-6 max-w-7xl mx-auto py-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
            {/* Left side - Content */}
            <div className="text-center lg:text-left space-y-6">
              {/* Main heading with refined typography and decorative line */}
              <div className="relative">
                <div className="absolute -left-4 top-8 w-1 h-32 bg-gradient-to-b from-red-500 to-transparent rounded-full hidden lg:block" />
                <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight leading-none">
                  Wake with
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-blue-500 mt-2">Drake</span>
                </h1>
              </div>

              {/* Subtitle with better spacing and visual separator */}
              <div className="space-y-4 pt-2">
                <div className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="h-px w-12 bg-gradient-to-r from-transparent to-red-500/50" />
                  <span className="text-red-500/70 text-sm font-semibold uppercase tracking-wider">New Release</span>
                  <div className="h-px w-12 bg-gradient-to-l from-transparent to-red-500/50" />
                </div>

                <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                  Bold, smooth, and dependable — the kind of cup you want before kickoff.
                </p>

                <p className="text-lg md:text-xl text-white/70 font-light">
                  For football mornings, family traditions, and passing down what matters. Because football mornings just hit different.
                </p>
              </div>

              {/* CTA buttons with improved styling */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                <button
                  onClick={handleOrderClick}
                  disabled={isSoldOut}
                  className={`group relative px-12 py-6 rounded-xl font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                    isSoldOut
                      ? 'bg-white/10 text-white/50 cursor-not-allowed'
                      : 'bg-gradient-to-r from-red-500 to-blue-500 text-white hover:from-red-600 hover:to-blue-600 shadow-2xl shadow-red-500/40 transform hover:scale-105 hover:shadow-red-500/60'
                  } overflow-hidden`}
                >
                  {!isSoldOut && <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />}
                  <Coffee className={`w-6 h-6 ${!isSoldOut && 'group-hover:rotate-12'} transition-transform`} />
                  {isSoldOut ? 'Sold Out' : 'Order Now'}
                </button>
                <button
                  onClick={scrollToDetails}
                  className="bg-white/5 text-white border-2 border-white/20 px-10 py-6 rounded-xl font-semibold text-lg hover:bg-white/10 hover:border-red-500/50 transition-all duration-300 backdrop-blur-md"
                >
                  Learn More
                </button>
              </div>
            </div>

            {/* Right side - Product image with enhanced styling */}
            <div className="flex items-center justify-center lg:justify-end relative">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-transparent rounded-3xl blur-3xl scale-110" />
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-red-500/30 to-transparent rounded-3xl blur-xl" />
                <img
                  src="/Drake2.png"
                  alt="Wake with Drake Coffee"
                  className="relative w-full max-w-md rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500 border-2 border-white/10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div id="details-section" className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About Wake with Drake
          </h2>
          <div className="w-20 h-1 bg-red-500 mx-auto rounded-full" />
        </div>

        <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-center gap-4 mb-8">
            <Coffee className="w-8 h-8 text-red-500" />
          </div>

          <h3 className="text-2xl md:text-3xl font-bold text-white text-center mb-6">
            Because Football Mornings Just Hit Different
          </h3>

          <div className="text-white/80 text-lg leading-relaxed max-w-3xl mx-auto space-y-4">
            <p>
              There's nothing better than watching your kids discover something you've loved forever — and somehow love it just as loudly.
            </p>
            <p>
              For us, that thing is football. What started as Adam and me watching games with coffee in hand has turned into kids asking questions, arguing calls, and running their own fantasy football league. Somewhere along the way, it stopped being just about the game and became about being together.
            </p>
            <p>
              Wake with Drake is our nod to football mornings, late games, early kickoffs, and the small rituals that stick around. It's for making coffee before the house wakes up (or explodes), for sitting on the couch together, and for passing down more than just favorite teams.
            </p>
            <p>
              The roast itself is bold, smooth, and dependable — the kind of cup you want before kickoff, school drop-off, or explaining why a perfectly good fantasy lineup somehow still lost.
            </p>
            <p className="font-medium text-white/90">
              Drink it during the game.<br />
              Drink it while talking trash with your kids.<br />
              Drink it because some traditions are worth waking up for.
            </p>
            <p className="text-xl font-bold text-red-500">
              Wake with Drake.<br />
              <span className="text-white/90">Because football mornings just hit different.</span>
            </p>
          </div>
        </div>
      </div>

      {/* Bean Composition Section - To be filled with provided data */}
      <div className="max-w-4xl mx-auto px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
          Coffee Composition
        </h2>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Placeholders for bean types */}
          <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-2xl p-8 text-center">
            <div className="text-4xl font-bold text-red-500 mb-2">[%]</div>
            <h3 className="text-xl font-semibold text-white mb-3">[Bean Name]</h3>
            <p className="text-white/60 text-sm mb-3">[Origin Details]</p>
            <div className="text-white/80 text-sm space-y-1">
              <p className="font-medium text-red-500">Notes:</p>
              <p>[Tasting Notes]</p>
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-12 space-y-6">
          <h3 className="text-2xl font-bold text-white mb-4">Tasting Profile</h3>
          <p className="text-white/80 leading-relaxed">
            Wake with Drake delivers a bold yet smooth experience with dependable flavor from start to finish. The perfect companion for early mornings, game days, and everything in between.
          </p>
          <div className="grid md:grid-cols-2 gap-4 pt-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Roast Level</h4>
              <p className="text-white/70">Medium</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h4 className="font-semibold text-white mb-2">Best Brewing Methods</h4>
              <p className="text-white/70">Drip, Pour Over, French Press</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing & Availability */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Order Today
          </h2>
          <p className="text-white/70 text-xl">Fresh roasted and ready to ship</p>
        </div>

        <div className="bg-gradient-to-br from-red-500/10 to-transparent border-2 border-red-500/40 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-baseline gap-2 mb-2">
              <span className="text-5xl md:text-6xl font-bold text-white">$19</span>
              <span className="text-white/70 text-lg">per 12oz bag</span>
            </div>
            <p className="text-red-500 font-semibold text-lg">
              Marblehead pricing
            </p>
          </div>

          <div className="space-y-3 mb-8">
            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-red-500" />
                <span className="text-white font-medium">Marblehead Delivery</span>
              </div>
              <span className="text-red-500 font-semibold text-lg">$19</span>
            </div>

            <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-red-500" />
                <span className="text-white font-medium">US Shipping (included)</span>
              </div>
              <span className="text-white/90 font-semibold text-lg">$27</span>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-red-500 to-blue-500 text-white hover:from-red-600 hover:to-blue-600 shadow-xl shadow-red-500/30 hover:scale-105 transform'
              }`}
            >
              <Coffee className="w-6 h-6" />
              {isSoldOut ? 'Sold Out' : 'Order Wake with Drake'}
            </button>
            <p className="text-white/60 text-sm mt-4">
              {isSoldOut ? 'Check back soon!' : 'Free local delivery • Shipping included for non-Marblehead orders'}
            </p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="border-t border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Wake with Drake?
          </h3>
          <p className="text-white/70 text-xl mb-8">
            Order your bag today and experience exceptional coffee
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleOrderClick}
              disabled={isSoldOut}
              className={`px-10 py-5 rounded-lg font-bold text-xl transition-all duration-300 inline-flex items-center justify-center gap-3 ${
                isSoldOut
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-red-500 to-blue-500 text-white hover:from-red-600 hover:to-blue-600 shadow-xl shadow-red-500/30'
              }`}
            >
              <Coffee className="w-6 h-6" />
              {isSoldOut ? 'Sold Out' : 'Order Now'}
            </button>
            <Link
              to="/"
              className="bg-white/10 text-white border border-white/20 px-8 py-5 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 inline-flex items-center justify-center"
            >
              Explore Bond Coffee
            </Link>
          </div>
          <p className="text-white/50 text-sm">
            Questions? Contact us at hello@bondcoffee.com
          </p>
        </div>
      </div>

      <Footer />

      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#132947] border-2 border-red-500/30 rounded-2xl max-w-2xl w-full p-8 relative shadow-2xl my-8 max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-10 bg-[#132947]/80 rounded-full p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className="text-3xl font-bold text-white mb-4 text-center">Choose Your Option</h2>
            <p className="text-white/70 text-center mb-8">Select delivery or shipping</p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Marblehead Delivery */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-red-500/50 transition-all group">
                <div className="text-center mb-4">
                  <MapPin className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Marblehead Delivery</h3>
                  <p className="text-white/70 text-sm mb-4">Free local delivery</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-red-500 mb-1">$19</div>
                  <div className="text-white/50 text-sm mb-3">per 12oz bag</div>
                </div>
                <a
                  href="https://buy.stripe.com/fZu14o7XF9552JD3Nw3VC0h"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-red-500 to-blue-500 text-white py-3 rounded-lg font-bold text-center hover:from-red-600 hover:to-blue-600 transition-all"
                >
                  Order - Marblehead
                </a>
              </div>

              {/* Shipping */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-red-500/50 transition-all group">
                <div className="text-center mb-4">
                  <Truck className="w-12 h-12 text-red-500 mx-auto mb-3" />
                  <h3 className="text-xl font-bold text-white mb-2">Ship Anywhere in the US</h3>
                  <p className="text-white/70 text-sm mb-4">Shipping included</p>
                </div>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-red-500 mb-1">$27</div>
                  <div className="text-white/50 text-sm mb-3">per 12oz bag + shipping</div>
                </div>
                <a
                  href="https://buy.stripe.com/8x2bJ2dhZfttckdck23VC0g"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gradient-to-r from-red-500 to-blue-500 text-white py-3 rounded-lg font-bold text-center hover:from-red-600 hover:to-blue-600 transition-all"
                >
                  Order - Shipped
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
